import { Box, Button, IconButton, TextField, Typography, Alert } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

// Each variant is a COMPLETE answer — one value per blank. The per-blank alternatives above
// ("blank 1 may be 7 or 3") cannot say which values go TOGETHER, so a question with two valid
// combinations accepts a mix of both (7 with 17, which is not a pair that sums to 20). Author the
// combinations here and they become the only accepted answers.
const AnswerVariantsEditor = ({ variants, setVariants, blankCount, blankLabels = [] }) => {
  const list = Array.isArray(variants) ? variants : [];

  const labelFor = (i) => {
    const raw = String(blankLabels?.[i] ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
    return raw || `Blank ${i + 1}`;
  };

  const addVariant = () => setVariants([...list, Array.from({ length: blankCount }, () => "")]);

  const removeVariant = (vi) => {
    const next = list.filter((_, i) => i !== vi);
    setVariants(next.length ? next : null);
  };

  const setValue = (vi, bi, value) => {
    setVariants(list.map((variant, i) => {
      if (i !== vi) return variant;
      const next = Array.from({ length: blankCount }, (_, k) => variant[k] ?? "");
      next[bi] = value;
      return next;
    }));
  };

  return (
    <Box sx={{ mt: 2, p: 1.5, border: "1px dashed #bbb", borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.5 }}>
        Complete alternative answers (optional)
      </Typography>
      <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 1 }}>
        Use this only when the question has several whole answers that must not be mixed — e.g. "two primes
        adding to 20" is 7 <b>and</b> 13, or 3 <b>and</b> 17, but never 7 and 17. Each row below is one
        complete answer, giving a value for every blank. Leave empty for normal questions, where each blank
        simply accepts its own alternatives.
      </Typography>

      {list.length > 0 && (
        <Alert severity="info" sx={{ py: 0, mb: 1 }}>
          <Typography variant="caption">
            While any rows are present, ONLY these combinations are accepted — the per-blank alternatives are
            ignored, and a part-matched row earns no marks. Tick "Answers can be in any order" above if the
            values may be entered in either blank.
          </Typography>
        </Alert>
      )}

      {list.map((variant, vi) => (
        <Box key={vi} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", minWidth: 70 }}>
            Answer {vi + 1}
          </Typography>
          {Array.from({ length: blankCount }, (_, bi) => (
            <TextField
              key={bi}
              size="small"
              label={labelFor(bi)}
              value={variant[bi] ?? ""}
              onChange={(e) => setValue(vi, bi, e.target.value)}
              sx={{ width: 150 }}
            />
          ))}
          <IconButton size="small" onClick={() => removeVariant(vi)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button size="small" startIcon={<Add />} onClick={addVariant} disabled={!blankCount}>
        Add complete answer
      </Button>
    </Box>
  );
};

export default AnswerVariantsEditor;
